<template>
  <div ref="root">
    <FrontendProductCarouselSection
      :title="title"
      :products="products"
      :loading="isLoading"
      :view-all-link="viewAllLink"
      :view-all-label="viewAllLabel"
      :accent-color-class="accentColorClass"
      :wrapper-class="wrapperClass"
      :empty-state-message="emptyStateMessage"
      :banner="banner"
      :banner-alt="bannerAlt"
      :banners="banners"
      @add-to-cart="(product) => emit('add-to-cart', product)"
      @toggle-favorite="(product) => emit('toggle-favorite', product)"
    >
      <template v-if="$slots.header" #header="slotProps">
        <slot name="header" v-bind="slotProps" />
      </template>

      <template v-if="$slots.action" #action="slotProps">
        <slot name="action" v-bind="slotProps" />
      </template>

      <template v-if="$slots.empty" #empty>
        <slot name="empty" />
      </template>
    </FrontendProductCarouselSection>
  </div>
</template>

<script setup>
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  fetcher: {
    type: Function,
    required: true
  },
  viewAllLink: {
    type: [String, Object],
    default: null
  },
  viewAllLabel: {
    type: String,
    default: 'Tümünü Gör'
  },
  accentColorClass: {
    type: [String, Array, Object],
    default: 'bg-orange-500'
  },
  wrapperClass: {
    type: [String, Array, Object],
    default: () => []
  },
  emptyStateMessage: {
    type: String,
    default: 'Gösterilecek ürün bulunamadı.'
  },
  banner: {
    type: [String, Object],
    default: null
  },
  bannerAlt: {
    type: String,
    default: ''
  },
  banners: {
    type: Array,
    default: () => []
  },
  initialProducts: {
    type: Array,
    default: () => []
  },
  rootMargin: {
    type: String,
    default: '200px 0px'
  }
})

const emit = defineEmits(['add-to-cart', 'toggle-favorite', 'loaded'])

const root = ref(null)
const products = ref(Array.isArray(props.initialProducts) ? [...props.initialProducts] : [])
const loading = ref(false)
const loaded = ref(products.value.length > 0)
let observer

const isLoading = computed(() => loading.value || (!loaded.value && products.value.length === 0))

const resolveProducts = async () => {
  const result = await props.fetcher()
  if (Array.isArray(result)) {
    return result
  }
  if (result && Array.isArray(result.data)) {
    return result.data
  }
  return []
}

const fetchProducts = async () => {
  if (loading.value || loaded.value) {
    return
  }

  loading.value = true

  try {
    const data = await resolveProducts()
    products.value = data
    emit('loaded', data)
  } catch (error) {
    console.error('Ürünler yüklenirken hata oluştu:', error)
  } finally {
    loading.value = false
    loaded.value = true
  }
}

const observe = () => {
  if (!root.value || loaded.value) {
    return
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        observer?.disconnect()
        fetchProducts()
      }
    })
  }, {
    rootMargin: props.rootMargin
  })

  observer.observe(root.value)
}

onMounted(() => {
  observe()
})

onBeforeUnmount(() => {
  observer?.disconnect()
})

watch(
  () => props.fetcher,
  () => {
    products.value = Array.isArray(props.initialProducts) ? [...props.initialProducts] : []
    loaded.value = products.value.length > 0
    if (!loaded.value && process.client) {
      nextTick(() => {
        observer?.disconnect()
        observe()
      })
    }
  }
)
</script>
